import type { Prompt, PromptSetWithCount } from '@meridian-ssa/shared';
import { useEffect, useState } from 'react';

import { CreatePromptModal } from '@/components/eval/CreatePromptModal';
import { CSVImporter } from '@/components/eval/CSVImporter';
import { EvalLayout } from '@/components/eval/EvalLayout';
import { Button } from '@/components/ui/Button';
import { InputField, TextareaField } from '@/components/ui/FormField';
import { Modal } from '@/components/ui/Modal';
import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

export default function PromptsPage() {
  const [sets, setSets] = useState<PromptSetWithCount[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [unassignedPrompts, setUnassignedPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [showCSVImporter, setShowCSVImporter] = useState(false);
  const [showCreateSet, setShowCreateSet] = useState(false);
  const [showAddPrompts, setShowAddPrompts] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedSetId) {
      loadSetPrompts(selectedSetId);
    } else {
      // Show unassigned prompts
      loadUnassignedPrompts();
    }
  }, [selectedSetId]);

  async function loadData() {
    setLoading(true);
    try {
      const [setsData, unassignedData] = await Promise.all([
        apiRequest('/api/eval/prompt-sets'),
        apiRequest('/api/eval/prompts/unassigned'),
      ]);

      setSets(setsData.promptSets || []);
      setUnassignedPrompts(unassignedData.prompts || []);

      // Default to unassigned
      if (!selectedSetId) {
        setPrompts(unassignedData.prompts || []);
      }
    } catch (e) {
      logError('Error loading prompt data', e);
    } finally {
      setLoading(false);
    }
  }

  async function loadSetPrompts(setId: string) {
    try {
      const data = await apiRequest(`/api/eval/prompt-sets/${setId}`);
      setPrompts(data.prompts || []);
    } catch (error) {
      logError('Error loading set prompts', error);
    }
  }

  async function loadUnassignedPrompts() {
    try {
      const data = await apiRequest('/api/eval/prompts/unassigned');
      setUnassignedPrompts(data.prompts || []);
      setPrompts(data.prompts || []);
    } catch (error) {
      logError('Error loading unassigned prompts', error);
    }
  }

  async function handleDeleteSet(setId: string) {
    if (!confirm('Are you sure you want to delete this prompt set?')) return;

    try {
      await apiRequest(`/api/eval/prompt-sets/${setId}`, {
        method: 'DELETE',
      });

      alert('Prompt set deleted successfully');
      if (selectedSetId === setId) {
        setSelectedSetId(null);
      }
      loadData();
    } catch (error: any) {
      alert(error.message);
    }
  }

  async function handleRemoveFromSet(promptId: string) {
    if (!selectedSetId) return;
    if (!confirm('Remove this prompt from the set?')) return;

    try {
      await apiRequest(
        `/api/eval/prompt-sets/${selectedSetId}/prompts?prompt_id=${promptId}`,
        { method: 'DELETE' }
      );

      alert('Prompt removed from set');
      loadSetPrompts(selectedSetId);
      loadData(); // Refresh counts and unassigned
    } catch (error: any) {
      alert(error.message);
    }
  }

  const isUnassignedView = selectedSetId === null;
  const currentSet = isUnassignedView
    ? null
    : sets.find(s => s.id === selectedSetId);

  return (
    <EvalLayout>
      <div>
        {/* Header */}
        <header className="mb-8 flex justify-between items-center">
          <div>
            <h1
              className="font-serif text-3xl font-medium m-0"
              style={{ color: '#1A1A1A' }}
            >
              Evaluation Prompts
            </h1>
            <p className="mt-2 text-sm" style={{ color: '#6B6B6B' }}>
              Organize prompts into sets for systematic testing
            </p>
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setShowCreateSet(true)}>
              + New Set
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowCSVImporter(true)}
            >
              Import CSV
            </Button>
            <Button variant="primary" onClick={() => setShowCreatePrompt(true)}>
              + Create Prompt
            </Button>
          </div>
        </header>

        {/* Set Tabs */}
        <div className="mb-8 flex gap-2 flex-wrap items-center">
          {/* Unassigned Tab */}
          <button
            onClick={() => setSelectedSetId(null)}
            className="py-2.5 px-4 rounded-lg cursor-pointer font-medium text-sm flex items-center gap-2 transition-all duration-150"
            style={{
              border: `1px solid ${isUnassignedView ? '#8B2332' : '#E8E0D8'}`,
              background: isUnassignedView ? '#8B2332' : 'white',
              color: isUnassignedView ? 'white' : '#1A1A1A',
            }}
          >
            Unassigned
            <span
              className="py-0.5 px-2 rounded-full text-xs"
              style={{
                background: isUnassignedView
                  ? 'rgba(255,255,255,0.2)'
                  : 'rgba(139,35,50,0.1)',
                color: isUnassignedView ? 'white' : '#8B2332',
              }}
            >
              {unassignedPrompts.length}
            </span>
          </button>

          {/* Set Tabs */}
          {sets.map(set => {
            const isActive = selectedSetId === set.id;
            return (
              <button
                key={set.id}
                onClick={() => setSelectedSetId(set.id)}
                className="py-2.5 px-4 rounded-lg cursor-pointer font-medium text-sm flex items-center gap-2 transition-all duration-150"
                style={{
                  border: `1px solid ${isActive ? '#8B2332' : '#E8E0D8'}`,
                  background: isActive ? '#8B2332' : 'white',
                  color: isActive ? 'white' : '#1A1A1A',
                }}
              >
                {set.name}
                <span
                  className="py-0.5 px-2 rounded-full text-xs"
                  style={{
                    background: isActive
                      ? 'rgba(255,255,255,0.2)'
                      : 'rgba(139,35,50,0.1)',
                    color: isActive ? 'white' : '#8B2332',
                  }}
                >
                  {set.prompt_count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Set Actions (when viewing a set) */}
        {!isUnassignedView && currentSet && (
          <div className="mb-6 flex gap-3 items-center">
            <Button size="small" onClick={() => setShowAddPrompts(true)}>
              Add Prompts to Set
            </Button>
            <Button
              size="small"
              variant="danger"
              onClick={() => selectedSetId && handleDeleteSet(selectedSetId)}
            >
              Delete Set
            </Button>
            {currentSet.description && (
              <span
                className="ml-auto text-sm italic"
                style={{ color: '#6B6B6B' }}
              >
                {currentSet.description}
              </span>
            )}
          </div>
        )}

        {/* Prompts Grid */}
        {loading ? (
          <div className="text-center py-16" style={{ color: '#A0A0A0' }}>
            Loading prompts...
          </div>
        ) : prompts.length === 0 ? (
          <div
            className="bg-white rounded-xl text-center py-16 px-8"
            style={{ border: '1px solid #E8E0D8' }}
          >
            <p className="m-0" style={{ color: '#A0A0A0' }}>
              {isUnassignedView
                ? 'All prompts are assigned to sets. Create a new prompt or import from CSV.'
                : "This set has no prompts yet. Click 'Add Prompts to Set' to add some."}
            </p>
          </div>
        ) : (
          <div
            className="grid gap-6"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            }}
          >
            {prompts.map(prompt => (
              <div
                key={prompt.id}
                onClick={() => setSelectedPrompt(prompt)}
                className="bg-white rounded-xl p-5 cursor-pointer transition-all duration-150 relative"
                style={{ border: '1px solid #E8E0D8' }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = '#8B2332';
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(139, 35, 50, 0.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#E8E0D8';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {!isUnassignedView && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      handleRemoveFromSet(prompt.id);
                    }}
                    className="absolute top-2 right-2 bg-transparent border-none cursor-pointer text-xl p-1 transition-colors"
                    style={{ color: '#A0A0A0' }}
                    onMouseEnter={e =>
                      (e.currentTarget.style.color = '#8B2332')
                    }
                    onMouseLeave={e =>
                      (e.currentTarget.style.color = '#A0A0A0')
                    }
                    title="Remove from set"
                  >
                    ×
                  </button>
                )}
                <div
                  className={`flex justify-between items-start mb-3 ${!isUnassignedView ? 'pr-8' : ''}`}
                >
                  <h3
                    className="text-lg font-semibold m-0"
                    style={{ color: '#1A1A1A' }}
                  >
                    {prompt.name}
                  </h3>
                  {(prompt.category || prompt.difficulty) && (
                    <div className="flex gap-2 shrink-0">
                      {prompt.category && (
                        <span
                          className="py-1 px-3 rounded-full text-xs font-medium"
                          style={{
                            background: 'rgba(139, 35, 50, 0.1)',
                            color: '#8B2332',
                          }}
                        >
                          {prompt.category}
                        </span>
                      )}
                      {prompt.difficulty && (
                        <span
                          className="py-1 px-3 rounded-full text-xs font-medium"
                          style={{
                            background: 'rgba(107, 107, 107, 0.1)',
                            color: '#6B6B6B',
                          }}
                        >
                          {prompt.difficulty}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <p
                  className="text-sm leading-relaxed mb-4 line-clamp-3"
                  style={{ color: '#6B6B6B' }}
                >
                  {prompt.text}
                </p>
                <div className="text-xs" style={{ color: '#A0A0A0' }}>
                  Created {new Date(prompt.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modals */}
        {showCreatePrompt && (
          <CreatePromptModal
            onClose={() => setShowCreatePrompt(false)}
            onSuccess={() => {
              setShowCreatePrompt(false);
              loadData();
            }}
          />
        )}

        {showCSVImporter && (
          <CSVImporter
            onClose={() => setShowCSVImporter(false)}
            onSuccess={() => {
              setShowCSVImporter(false);
              loadData();
            }}
          />
        )}

        {showCreateSet && (
          <CreateSetModal
            onClose={() => setShowCreateSet(false)}
            onSuccess={() => {
              setShowCreateSet(false);
              loadData();
            }}
          />
        )}

        {showAddPrompts && selectedSetId && (
          <AddPromptsModal
            setId={selectedSetId}
            onClose={() => setShowAddPrompts(false)}
            onSuccess={() => {
              setShowAddPrompts(false);
              loadSetPrompts(selectedSetId);
              loadData();
            }}
          />
        )}

        {selectedPrompt && (
          <PromptDetailModal
            prompt={selectedPrompt}
            onClose={() => setSelectedPrompt(null)}
          />
        )}
      </div>
    </EvalLayout>
  );
}

// Create Set Modal
const CreateSetModal = ({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit() {
    try {
      setLoading(true);
      setError('');

      await apiRequest('/api/eval/prompt-sets', {
        method: 'POST',
        body: JSON.stringify({ name, description }),
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title="Create New Prompt Set" onClose={onClose}>
      <div className="flex flex-col gap-4">
        {error && (
          <div
            className="p-3 rounded-lg text-sm"
            style={{ background: 'rgba(196, 30, 58, 0.1)', color: '#C41E3A' }}
          >
            {error}
          </div>
        )}

        <InputField
          label="Set Name"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g., Q1 2025 Test Cases"
          required
        />

        <TextareaField
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional description"
        />

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || !name}
            loading={loading}
          >
            Create Set
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Add Prompts Modal
const AddPromptsModal = ({
  setId,
  onClose,
  onSuccess,
}: {
  setId: string;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [availablePrompts, setAvailablePrompts] = useState<Prompt[]>([]);
  const [selectedPrompts, setSelectedPrompts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAvailablePrompts();
  }, []);

  async function loadAvailablePrompts() {
    try {
      // Get unassigned prompts
      const data = await apiRequest('/api/eval/prompts/unassigned');
      setAvailablePrompts(data.prompts || []);
    } catch (err) {
      logError('Error loading available prompts', err);
      setError('Failed to load available prompts');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    try {
      setSubmitting(true);
      setError('');

      await apiRequest(`/api/eval/prompt-sets/${setId}/prompts`, {
        method: 'POST',
        body: JSON.stringify({ prompt_ids: selectedPrompts }),
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  function togglePrompt(id: string) {
    setSelectedPrompts(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  }

  return (
    <Modal title="Add Prompts to Set" onClose={onClose} maxWidth="700px">
      <div className="flex flex-col gap-4">
        {error && (
          <div
            className="p-3 rounded-lg text-sm"
            style={{ background: 'rgba(196, 30, 58, 0.1)', color: '#C41E3A' }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8" style={{ color: '#A0A0A0' }}>
            Loading...
          </div>
        ) : availablePrompts.length === 0 ? (
          <div className="text-center py-8" style={{ color: '#A0A0A0' }}>
            No unassigned prompts available. All prompts are already in sets.
          </div>
        ) : (
          <>
            <div className="text-sm" style={{ color: '#6B6B6B' }}>
              Select prompts to add ({selectedPrompts.length} selected)
            </div>
            <div className="max-h-[400px] overflow-y-auto flex flex-col gap-2">
              {availablePrompts.map(prompt => {
                const isSelected = selectedPrompts.includes(prompt.id);
                return (
                  <label
                    key={prompt.id}
                    className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                    style={{
                      border: `2px solid ${isSelected ? '#8B2332' : '#E8E0D8'}`,
                      background: isSelected
                        ? 'rgba(139, 35, 50, 0.05)'
                        : 'white',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => togglePrompt(prompt.id)}
                      className="mt-0.5 shrink-0"
                      style={{ accentColor: '#8B2332' }}
                    />
                    <div className="flex-1">
                      <div className="font-medium" style={{ color: '#1A1A1A' }}>
                        {prompt.name}
                      </div>
                      <div
                        className="text-sm mt-1"
                        style={{ color: '#6B6B6B' }}
                      >
                        {prompt.text.substring(0, 100)}...
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting || selectedPrompts.length === 0}
            loading={submitting}
          >
            Add {selectedPrompts.length} Prompt
            {selectedPrompts.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Prompt Detail Modal
const PromptDetailModal = ({
  prompt,
  onClose,
}: {
  prompt: Prompt;
  onClose: () => void;
}) => {
  return (
    <Modal
      title={prompt.name || 'Prompt Details'}
      onClose={onClose}
      maxWidth="700px"
    >
      <div className="flex flex-col gap-6">
        {(prompt.category || prompt.difficulty) && (
          <div className="flex gap-2">
            {prompt.category && (
              <span
                className="py-1 px-3 rounded-full text-sm font-medium"
                style={{
                  background: 'rgba(139, 35, 50, 0.1)',
                  color: '#8B2332',
                }}
              >
                {prompt.category}
              </span>
            )}
            {prompt.difficulty && (
              <span
                className="py-1 px-3 rounded-full text-sm font-medium"
                style={{
                  background: 'rgba(107, 107, 107, 0.1)',
                  color: '#6B6B6B',
                }}
              >
                {prompt.difficulty}
              </span>
            )}
          </div>
        )}

        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Prompt Text
          </label>
          <div
            className="p-4 rounded-lg whitespace-pre-wrap leading-relaxed text-sm"
            style={{
              background: '#F5F0EB',
              border: '1px solid #E8E0D8',
              color: '#1A1A1A',
            }}
          >
            {prompt.text}
          </div>
        </div>

        <div
          className="text-xs pt-4"
          style={{ borderTop: '1px solid #E8E0D8', color: '#A0A0A0' }}
        >
          Created {new Date(prompt.created_at).toLocaleString()}
        </div>
      </div>
    </Modal>
  );
};
